import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, setDoc, updateDoc, increment, runTransaction, Timestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { FaFileUpload, FaKeyboard, FaTable, FaFileDownload, FaTrash } from "react-icons/fa";

// Function to normalize string for comparison (removes all spaces, uppercase)
const normalize = (str) => (str || "").replace(/\s+/g, "").toUpperCase();

const CnfPurchase = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [skuMaster, setSkuMaster] = useState({});
    const [skuList, setSkuList] = useState([]);
    
    // Masters for dropdowns
    const [allSuppliers, setAllSuppliers] = useState([]);
    const [allCnf, setAllCnf] = useState([]);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("UPLOAD"); // UPLOAD | MANUAL

    // Header Metadata
    const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
    const [invoiceNo, setInvoiceNo] = useState("");
    const [ewayBillNo, setEwayBillNo] = useState("");
    const [lrNumber, setLrNumber] = useState("");
    const [vehicleNo, setVehicleNo] = useState("");
    const [remarks, setRemarks] = useState("");
    
    // Controlled Selects
    const [supplierId, setSupplierId] = useState("");
    const [cnfId, setCnfId] = useState("");

    // Line Items
    const [items, setItems] = useState([]); 

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                if (!["superadmin", "admin", "operator"].includes(profile.role)) {
                    alert("Unauthorized access");
                    navigate("/dashboard");
                    return;
                }
                setUserProfile(profile);
                loadMasters();
            }
        });
        return () => unsub();
    }, [navigate]);

    const loadMasters = async () => {
        try {
            // SKUs
            const skuSnap = await getDocs(query(collection(db, "skuMaster")));
            const smap = {};
            const slist = [];
            skuSnap.forEach(d => {
                const data = d.data();
                const skuId = (data.sku || data.Name || "").trim().toUpperCase();
                smap[skuId] = data;
                slist.push(skuId);
            });
            setSkuMaster(smap);
            setSkuList(slist);

            // Suppliers
            const sSnap = await getDocs(query(collection(db, "suppliers")));
            const slist2 = [];
            sSnap.forEach(d => slist2.push({ id: d.id, ...d.data() }));
            setAllSuppliers(slist2);

            // C&F
            const cSnap = await getDocs(query(collection(db, "cnf_master")));
            const clist = [];
            cSnap.forEach(d => clist.push({ id: d.id, ...d.data() }));
            setAllCnf(clist);

        } catch (err) {
            console.error("Error loading masters:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        setItems([...items, { sku: "", name: "", hsn: "", qtyPkt: 0, qtyBags: 0, rate: 0, discount: 0, igst: 0, gstRate: 0, total: 0, match: true }]);
    };

    const handleRemoveRow = (idx) => {
        setItems(items.filter((_, i) => i !== idx));
    };

    const updateRow = (idx, field, val) => {
        const newItems = [...items];
        newItems[idx][field] = val;
        
        if (field === "sku" || field === "name") {
            const searchVal = normalize(val);
            const matchedSku = skuList.find(s => normalize(s) === searchVal || normalize(skuMaster[s]?.Name) === searchVal);
            if (matchedSku) {
                const master = skuMaster[matchedSku];
                newItems[idx].sku = matchedSku;
                newItems[idx].name = master.Name;
                newItems[idx].hsn = master.hsn || master.HSN || "";
                newItems[idx].match = true;
            } else {
                newItems[idx].match = false;
            }
        }

        if (field === "qtyBags" && newItems[idx].sku) {
            const m = skuMaster[newItems[idx].sku];
            if (m) {
                const c_Bag = Number(m.cartonsPerBag) || 1;
                const c_Ctn = Number(m.packetsPerCarton) || 1;
                newItems[idx].qtyPkt = val * c_Bag * c_Ctn;
            }
        }
        
        // Auto-recalculate total for manual entry
        if (["qtyBags", "rate", "discount", "igst"].includes(field)) {
            const it = newItems[idx];
            newItems[idx].total = (Number(it.qtyBags) * Number(it.rate)) - Number(it.discount) + Number(it.igst);
        }

        setItems(newItems);
    };

    // Neural Simulator: Benchmarking against VRPL - Bill-03 sample
    const runSimulator = () => {
        const simText = `
            Tax Invoice (Page 1)
            Seller: VIIKING REFRESHMENT PRIVATE LIMITED
            GSTIN/UIN: 36AAICV5131A1ZO
            Consignee: SUPREME ENTERPRISES
            GSTIN/UIN: 29AFAPC3951P1Z6
            Invoice No: 003/2025-26
            Dated 10-Mar-2026
            e-Way Bill No. 1623 6811 0271
            Motor Vehicle No. KA51AL6354
            LR-RR No. 7730 dt. 10-Mar-2026
            
            Description of Goods | HSN | Quantity | Rate | Amount
            1 Goa-1000 Supari Mix ( MRP-5/-PP) | 21069030 | 89 BAG | 29,520.00 | 26,27,280.00
            
            Less: Trade Discount (-) 8,26,560.00
            Less: Trade Discount (-) 1,47,600.00
            Output - IGST 5% 82,656.00
            Total: 17,35,776.00

            Remarks:
            Being Sale of 89 Bags ( 1 Bags = 10 Cartons, 1 Cartons = 31 Packets ). Trade Discount for 28 Bags (2:1 Sch) + 5 Bags = 33 Bags.
        `;
        processRawText(simText);
    };

    const processRawText = (fullText) => {
        console.log("NEURAL PROCESSING INITIATED...");
        const textUpper = fullText.toUpperCase();
        
        const findMeta = (keys) => {
            for (let k of keys) {
                const idx = textUpper.indexOf(k.toUpperCase());
                if (idx !== -1) {
                    let chunk = fullText.substring(idx + k.length, idx + k.length + 80);
                    const cleanChunk = chunk.replace(/[|:;]/g, " ").trim();
                    const parts = cleanChunk.split(/\s{2,}|\n|\r/);
                    const val = parts.find(p => p.trim().length > 1);
                    if (val) return val.trim();
                }
            }
            return "";
        };

        setInvoiceNo(findMeta(["Invoice No.", "Invoice No", "Inv No"]));
        const eway = findMeta(["e-Way Bill No.", "eWay Bill", "EWAY BILL"]);
        if (eway) setEwayBillNo(eway.replace(/\s/g, ''));
        
        setVehicleNo(findMeta(["Motor Vehicle No.", "Vehicle No", "Vehicle"]));
        setLrNumber(findMeta(["LR-RR No.", "LR No", "Bill of Lading", "RR No"]));
        
        // Remarks Extraction
        const rem = findMeta(["Remarks", "Being", "Notes"]);
        if (rem) setRemarks(rem);

        // Date Parsing
        const rawDateStr = findMeta(["Dated", "Date", "Invoice Date"]);
        if (rawDateStr) {
             const dMatch = rawDateStr.match(/(\d{1,2})[\s\-\/]([A-Za-z]{3}|\d{1,2})[\s\-\/](20\d{2}|2\d)/);
             if (dMatch) {
                const months = { JAN: "01", FEB: "02", MAR: "03", APR: "04", MAY: "05", JUN: "06", JUL: "07", AUG: "08", SEP: "09", OCT: "10", NOV: "11", DEC: "12" };
                let m = dMatch[2].toUpperCase();
                m = months[m] || m.padStart(2, '0');
                let y = dMatch[3]; if (y.length === 2) y = "20" + y;
                setPurchaseDate(`${y}-${m}-${dMatch[1].padStart(2, '0')}`);
             }
        }

        // Master Matching
        const textCompact = ultraNorm(fullText);
        const sMatch = allSuppliers.find(s => textCompact.includes(ultraNorm(s.gst)) || textCompact.includes(ultraNorm(s.name)));
        if (sMatch) setSupplierId(sMatch.id);
        const cMatch = allCnf.find(c => textCompact.includes(ultraNorm(c.gst)) || textCompact.includes(ultraNorm(c.name)));
        if (cMatch) setCnfId(cMatch.id);

        const discoveredItems = [];
        const lines = fullText.split(/\n/);
        
        lines.forEach((line, lineIdx) => {
            const lineUpper = line.toUpperCase();
            const matchedSku = skuList.find(skuKey => {
                const master = skuMaster[skuKey];
                const name = (master.Name || skuKey).toUpperCase();
                return lineUpper.includes(name) || name.includes(lineUpper.trim());
            });

            if (matchedSku) {
                const master = skuMaster[matchedSku];
                const window = lines.slice(lineIdx, lineIdx + 12).join(" ").toUpperCase();
                
                // HSN Extraction: Look for 4-8 digit number near SKU
                const hsnMatch = window.match(/(\b\d{4,8}\b)/);
                let hsn = hsnMatch ? hsnMatch[1] : (master.hsn || master.HSN || "");

                // Qty
                const qRegex = /(\d+)[\s|]*(BAG|BOX|CTN|PKT|PKTS)/i;
                const qMatch = window.match(qRegex);
                let qty = qMatch ? Number(qMatch[1]) : 0;

                // Rate
                const rRegex = /(?:BAG|BOX|CTN|RATE)[\s|]*[:]?[\s|]*([\d,]+\.\d{2})/i;
                const rMatch = window.match(rRegex);
                let rate = rMatch ? Number(rMatch[1].replace(/,/g, '')) : 0;

                // Discounts (Trade Discount)
                let totalDiscount = 0;
                const discLines = lines.slice(lineIdx, lineIdx + 12).filter(l => l.toUpperCase().includes("DISCOUNT") || l.toUpperCase().includes("LESS"));
                discLines.forEach(l => {
                    const dMatch = l.match(/([\d,]+\.\d{2})/);
                    if (dMatch) totalDiscount += Number(dMatch[1].replace(/,/g, ''));
                });

                // IGST & GST Rate
                const igstMatch = window.match(/IGST\s*(?:OUTPUT)?\s*(\d{1,2})?%?\s*([\d,]+\.\d{2})/i);
                let gstRate = igstMatch && igstMatch[1] ? Number(igstMatch[1]) : 0;
                let igstAmt = igstMatch ? Number(igstMatch[2].replace(/,/g, '')) : 0;

                if (qty > 0 || rate > 0) {
                    discoveredItems.push({
                        sku: matchedSku,
                        name: master.Name || matchedSku,
                        hsn: hsn,
                        qtyBags: qty,
                        qtyPkt: qty * (Number(master.cartonsPerBag)||1) * (Number(master.packetsPerCarton)||1),
                        rate: rate,
                        discount: totalDiscount,
                        igst: igstAmt,
                        gstRate: gstRate,
                        total: (qty * rate) - totalDiscount + igstAmt,
                        match: true
                    });
                }
            }
        });

        if (discoveredItems.length > 0) {
            setItems(discoveredItems);
        } else {
             const fallback = [];
             skuList.forEach(sk => {
                if (textCompact.includes(ultraNorm(skuMaster[sk].Name || sk))) {
                    fallback.push({ sku: sk, name: skuMaster[sk].Name, hsn: skuMaster[sk].hsn||"", qtyBags: 0, qtyPkt: 0, rate: 0, total: 0, discount: 0, igst: 0, gstRate: 0, match: true });
                }
             });
             setItems(fallback);
        }
    };

    // Ultra-Robust Normalization (Removes spaces and all symbols like dash, dot, etc)
    const ultraNorm = (str) => (str || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            const pdfjsLib = window['pdfjs-dist/build/pdf'];
            if (!pdfjsLib) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
                    script.onload = resolve;
                    document.head.appendChild(script);
                });
                await new Promise(r => setTimeout(r, 500));
            }
            
            const pdfjs = window['pdfjs-dist/build/pdf'];
            pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
            
            let fullText = "";
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                // Join with space to keep tokens separate but searchable
                fullText += textContent.items.map(item => item.str).join(" ") + "\n";
            }

            console.log("PDF RAW TEXT:", fullText);
            processRawText(fullText);

        } catch (err) {
            console.error("PDF PARSE ERROR:", err);
            alert("Neural scanner encountered a structural anomaly. Error details in console.");
        } finally {
            setLoading(false);
        }
    };

    const handleSavePurchase = async () => {
        if (!invoiceNo || !supplierId || !cnfId) { alert("Missing Header Details"); return; }
        const validItems = items.filter(it => it.sku && it.qtyPkt > 0);
        if (validItems.length === 0) { alert("Add valid items"); return; }

        setSubmitting(true);
        try {
            const timestamp = Timestamp.now();
            const supplierData = allSuppliers.find(s => s.id === supplierId);
            const cnfData = allCnf.find(c => c.id === cnfId);
            const clientId = userProfile?.clientId || auth.currentUser?.uid || "default";
            const currentUid = auth.currentUser?.uid;

            for (const it of validItems) {
                const purchasePayload = {
                    clientId: clientId,
                    date: purchaseDate,
                    invoiceNo, ewayBillNo, lrNumber, vehicleNo,
                    supplierId,
                    supplierName: supplierData.name,
                    supplierGst: supplierData.gst,
                    cnfId,
                    cnfName: cnfData.name,
                    cnfGst: cnfData.gst,
                    sku: it.sku.trim().toUpperCase(),
                    qtyPkt: Number(it.qtyPkt),
                    qtyBags: Number(it.qtyBags),
                    rate: Number(it.rate),
                    hsn: it.hsn || "",
                    discount: Number(it.discount),
                    gstRate: Number(it.gstRate),
                    igst: Number(it.igst),
                    total: Number(it.total),
                    remarks: remarks,
                    createdAt: timestamp,
                    createdBy: currentUid
                };
                
                // 1. Save to C&F Purchase Module
                await addDoc(collection(db, "cnf_purchases"), purchasePayload);

                // 2. Transcribe to Universal Inward Ledger (For Register Clarity)
                await addDoc(collection(db, "inward_register"), {
                    inwardDate: purchaseDate,
                    sku: it.sku.trim().toUpperCase(),
                    quantity: Number(it.qtyPkt),
                    purchaseBasicRate: Number(it.rate),
                    source: supplierData.name,
                    reference: invoiceNo,
                    uploadedBy: currentUid,
                    uploadedAt: timestamp,
                    clientId: clientId,
                    sourceType: "CNF_PURCHASE"
                });
            }

            const stockRef = doc(db, "cnf_stock_daily", `${clientId}_${purchaseDate}`);
            await setDoc(stockRef, { clientId: clientId, date: purchaseDate, initialized: true }, { merge: true });

            const inventoryUpdates = {};
            validItems.forEach(it => {
                inventoryUpdates[`inventory.${it.sku.toUpperCase()}.purchase`] = increment(Number(it.qtyPkt));
            });
            await updateDoc(stockRef, { ...inventoryUpdates, updatedAt: serverTimestamp() });

            alert("Purchase Ledger Updated");
            navigate("/cnf-stock-ledger");
        } catch (err) {
            alert(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading && skuList.length === 0) return <Layout><div style={{ padding: 100, textAlign: "center" }}>Authorizing Primary Systems...</div></Layout>;

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40 }}>
                    <h1 style={{ fontSize: 32, fontWeight: 900 }}>C&F ACQUISITION ENGINE</h1>
                    <p style={{ color: "var(--text-muted)" }}>Centralizing manufacturing influx and supplier reconciliation.</p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 32 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                        
                        {/* TAB NAV */}
                        <div style={{ display: "flex", gap: 12 }}>
                            <button onClick={() => setActiveTab("UPLOAD")} className={`tab-btn ${activeTab === "UPLOAD" ? "active" : ""}`}><FaFileUpload /> <span>SMART UPLOAD</span></button>
                            <button onClick={() => setActiveTab("MANUAL")} className={`tab-btn ${activeTab === "MANUAL" ? "active" : ""}`}><FaKeyboard /> <span>MANUAL</span></button>
                        </div>

                        {/* MASTER METADATA */}
                        <div className="premium-card">
                            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 20, marginBottom: 20 }}>
                                <div><label style={labelStyle}>INVOICE NO</label><input className="form-input" value={invoiceNo} onChange={e=>setInvoiceNo(e.target.value)} /></div>
                                <div><label style={labelStyle}>DATE</label><input type="date" className="form-input" value={purchaseDate} onChange={e=>setPurchaseDate(e.target.value)} /></div>
                                <div><label style={labelStyle}>E-WAY BILL</label><input className="form-input" value={ewayBillNo} onChange={e=>setEwayBillNo(e.target.value)} /></div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                                <div><label style={labelStyle}>LR / RR NO</label><input className="form-input" value={lrNumber} onChange={e=>setLrNumber(e.target.value)} /></div>
                                <div><label style={labelStyle}>VEHICLE NO</label><input className="form-input" value={vehicleNo} onChange={e=>setVehicleNo(e.target.value)} /></div>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>REMARKS (EXTRACTED)</label>
                                <textarea className="form-input" style={{ height: 60, resize: "none" }} value={remarks} onChange={e=>setRemarks(e.target.value)} placeholder="Narrative, overrides, or PDF notes..." />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                                <div>
                                    <label style={labelStyle}>REGISTERED SUPPLIER</label>
                                    <select className="form-input" value={supplierId} onChange={e=>setSupplierId(e.target.value)}>
                                        <option value="">Select Supplier...</option>
                                        {allSuppliers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.gst})</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>C&F ENTITY</label>
                                    <select className="form-input" value={cnfId} onChange={e=>setCnfId(e.target.value)}>
                                        <option value="">Select C&F...</option>
                                        {allCnf.map(c => <option key={c.id} value={c.id}>{c.name} ({c.gst})</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {activeTab === "UPLOAD" && items.length === 0 ? (
                            <div className="premium-card" style={{ padding: 60, textAlign: "center", border: "2px dashed var(--border)" }}>
                                <input type="file" id="pdf-in" hidden onChange={handleFileUpload} accept=".pdf" />
                                <label htmlFor="pdf-in" style={{ cursor: "pointer" }}>
                                    <div style={{ fontSize: 48, color: "var(--primary)", marginBottom: 20 }}><FaFileUpload /></div>
                                    <h3 style={{ fontSize: 20, marginBottom: 8 }}>Load Neural Parser</h3>
                                    <p style={{ color: "var(--text-muted)" }}>System will auto-detect Supplier, C&F, and SKU quantities.</p>
                                    <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
                                        <button className="btn-primary">SELECT PDF</button>
                                        <button type="button" className="btn-secondary" onClick={(e) => { e.preventDefault(); runSimulator(); }} style={{ background: "rgba(0,0,0,0.05)" }}>SIMULATE VRPL V3</button>
                                    </div>
                                </label>
                            </div>
                        ) : (
                            <div className="premium-card" style={{ padding: 0 }}>
                                <div style={{ overflowX: "auto" }}>
                                    <table className="enterprise-table">
                                        <thead>
                                            <tr>
                                                <th>ITEM DESCRIPTION (SKU)</th>
                                                <th>HSN</th>
                                                <th>BAGS</th>
                                                <th>PKTS</th>
                                                <th>RATE</th>
                                                <th>DISC (-)</th>
                                                <th>TAX (%)</th>
                                                <th>IGST (+)</th>
                                                <th>TOTAL</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.map((it, idx) => (
                                                <tr key={idx} style={{ background: !it.match ? "rgba(239, 68, 68, 0.05)" : "transparent" }}>
                                                    <td style={{ minWidth: 200 }}>
                                                        <input 
                                                            className="form-input" 
                                                            placeholder="Search Sku Master..."
                                                            style={{ fontWeight: 700, color: it.match ? "var(--primary)" : "var(--error)" }}
                                                            value={it.name || it.sku} 
                                                            onChange={e=>updateRow(idx, "name", e.target.value)} 
                                                        />
                                                        {!it.match && <div style={{ fontSize: 9, color: "var(--error)", marginTop: 2 }}>NOT IN MASTER</div>}
                                                    </td>
                                                    <td style={{ width: 100 }}>
                                                        <input className="form-input" placeholder="HSN" value={it.hsn} onChange={e=>updateRow(idx, "hsn", e.target.value)} />
                                                    </td>
                                                    <td style={{ width: 70 }}><input className="form-input" type="number" value={it.qtyBags} onChange={e=>updateRow(idx, "qtyBags", Number(e.target.value))} /></td>
                                                    <td style={{ width: 80 }}><input className="form-input" type="number" value={it.qtyPkt} readOnly /></td>
                                                    <td style={{ width: 100 }}><input className="form-input" type="number" value={it.rate} onChange={e=>updateRow(idx, "rate", Number(e.target.value))} /></td>
                                                    <td style={{ width: 90 }}><input className="form-input" type="number" value={it.discount} onChange={e=>updateRow(idx, "discount", Number(e.target.value))} /></td>
                                                    <td style={{ width: 70 }}><input className="form-input" type="number" value={it.gstRate} onChange={e=>updateRow(idx, "gstRate", Number(e.target.value))} /></td>
                                                    <td style={{ width: 90 }}><input className="form-input" type="number" value={it.igst} onChange={e=>updateRow(idx, "igst", Number(e.target.value))} /></td>
                                                    <td style={{ width: 110 }}><input className="form-input" type="number" value={it.total} readOnly /></td>
                                                    <td><button onClick={()=>handleRemoveRow(idx)} style={{ color: "var(--error)", border: "none", background: "none", cursor: "pointer" }}><FaTrash/></button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ padding: 20, textAlign: "center", borderTop: "1px solid var(--border)" }}>
                                    <button className="btn-secondary" onClick={handleAddRow}>+ ADD LINE MANUALLY</button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{ position: "sticky", top: 100 }}>
                        <div className="premium-card" style={{ background: "var(--text-main)", color: "white", padding: 28 }}>
                            <h3 style={{ margin: "0 0 24px 0", fontSize: 13, fontWeight: 800, opacity: 0.6 }}>COMMITMENT SUMMARY</h3>
                            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Varieties</span><span style={{ fontWeight: 700 }}>{items.length}</span></div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Total Bags</span><span style={{ fontWeight: 700 }}>{items.reduce((s,i)=>s+Number(i.qtyBags),0)}</span></div>
                                <div style={{ width: "100%", height: 1, background: "rgba(255,255,255,0.1)" }}></div>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--success)" }}>₹{items.reduce((s,i)=>s+Number(i.total || (i.qtyBags * i.rate)),0).toLocaleString()}</div>
                                    <div style={{ fontSize: 10, opacity: 0.5 }}>LEDGER IMPACT TOTAL</div>
                                </div>
                                <button className="btn-primary" style={{ background: "var(--success)", border: "none", width: "100%", padding: 18, fontSize: 16 }} onClick={handleSavePurchase} disabled={submitting}>SYNC TO LEDGER</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .tab-btn { padding: 14px 24px; border: 1px solid var(--border); background: white; border-radius: 12px; cursor: pointer; display: flex; align-items: center; gap: 10px; font-weight: 800; color: var(--text-muted); transition: all 0.2s; }
                .tab-btn.active { background: var(--primary); color: white; border-color: var(--primary); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3); }
            `}</style>
        </Layout>
    );
};

const labelStyle = { display: "block", fontSize: 10, fontWeight: 800, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase" };

export default CnfPurchase;
